import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, Disc3 } from 'lucide-react';
import { formatGenreLabel } from '../../../utils/genreLabel';

interface CommonInterestButtonProps {
  commonInterest: {
    type: string;
    value: string;
  };
  isGridView: boolean;
  isMobile: boolean;
}

const CommonInterestButton: React.FC<CommonInterestButtonProps> = ({
    commonInterest,
    isGridView,
    isMobile
}) => {
    const navigate = useNavigate();
    const buttonRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        // Once visible, no need to keep observing
                        if (buttonRef.current) {
                            observer.unobserve(buttonRef.current);
                        }
                    }
                });
            },
            { threshold: 0.1 } // Start animation when 10% of the element is visible
        );

        if (buttonRef.current) {
            observer.observe(buttonRef.current);
        }

        return () => {
            if (buttonRef.current) {
                observer.unobserve(buttonRef.current);
            }
        };
    }, []);

    const Icon = commonInterest.type === 'artist' ? Music : Disc3;
    const iconColor = commonInterest.type === 'artist' ? 'text-purple-500' : 'text-indigo-500';
    const formattedValue = commonInterest.type === 'genre'
        ? formatGenreLabel(commonInterest.value)
        : commonInterest.value;

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/search?query=${encodeURIComponent(formattedValue)}`);
    };

    const animationStyles = `
        @keyframes blurIn {
            0% { filter: blur(10px); }
            85% { filter: blur(8px); }
            92% { filter: blur(5px); }
            96% { filter: blur(2px); }
            100% { filter: blur(0); }
        }
        
        @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
        }
        
        .blur-in {
            animation: blurIn 1.5s ease-out forwards;
            animation-fill-mode: forwards;
        }
        
        .pulse-hover:hover {
            animation: pulse 1.5s infinite;
        }
    `;

    // Button styling without the pulse hover animation
    const buttonClass = `inline-flex items-center bg-indigo-100 font-medium rounded-full 
        shadow-sm hover:bg-indigo-200 transition-colors duration-300 
        ${isGridView ? 'text-[9px] py-0.5 mt-0.5 max-w-[100px]' : isMobile ? 'text-[10px] py-0.5' : 'text-xs py-1'}`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
            <div
                ref={buttonRef}
                className={isVisible ? 'blur-in' : ''}
                style={{ filter: !isVisible ? 'blur(10px)' : undefined }}
            >
                <div
                    className={`${buttonClass} pulse-hover`}
                    onClick={handleClick}
                    title={`Search for ${formattedValue}`}
                >
                    <Icon
                        size={isGridView || isMobile ? 9 : 12}
                        className={`mr-1 ${iconColor} flex-shrink-0 ml-1.5`}
                    />
                    <span className="truncate mr-1.5 text-indigo-700">
                        {isGridView ? formattedValue : `Fan of ${formattedValue}`}
                    </span>
                </div>
            </div>
        </>
    );
};

export default CommonInterestButton;
